(() => {
  const instances = new WeakMap();

  class ProductStickyAtc {
    constructor(root) {
      this.root = root;
      this.container = root.querySelector('[data-sticky-atc]');
      this.button = this.container?.querySelector('[data-sticky-atc-button]');
      this.topButton = this.container?.querySelector('[data-sticky-atc-top]');
      this.label = this.container?.querySelector('[data-sticky-atc-label]');
      this.titleEl = this.container?.querySelector('[data-sticky-atc-title]');
      this.ratingEl = this.container?.querySelector('[data-sticky-atc-rating]');
      this.sizeEl = this.container?.querySelector('[data-sticky-atc-size]');
      this.customizeEl = this.container?.querySelector('[data-sticky-atc-customize]');
      this.priceEl = this.container?.querySelector('[data-sticky-atc-price]');
      this.compareEl = this.container?.querySelector('[data-sticky-atc-compare]');
      this.sectionId = root.dataset.section;
      this.primaryButton = root.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      this.form = root.querySelector(`#product-form-${this.sectionId}`);
      this.priceSource = root.querySelector(`#price-${this.sectionId}`);
      this.titleSource = root.querySelector('.product__title h1');
      this.ratingSource = root.querySelector('.rating-wrapper');
      this.customizePanel = root.querySelector('[data-customize-panel]');
      this.customizeNameInput = root.querySelector('[data-customize-input="name"]');
      this.customizeNumberInput = root.querySelector('[data-customize-input="number"]');
      this.intersectionObserver = null;
      this.mutationObserver = null;
      this.priceObserver = null;
      this.variantObserver = null;
      this.customizeObserver = null;
      this.variantUnsubscriber = null;
      this.isPrimaryVisible = true;

      this.onScroll = this.onScroll.bind(this);
      this.onStickyClick = this.onStickyClick.bind(this);
      this.onTopClick = this.onTopClick.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.syncButtonState = this.syncButtonState.bind(this);
      this.onOptionChange = this.onOptionChange.bind(this);
    }

    init() {
      if (!this.container || !this.button || !this.primaryButton || !this.form) return;

      this.button.addEventListener('click', this.onStickyClick);
      this.topButton?.addEventListener('click', this.onTopClick);
      this.form.addEventListener('submit', this.onFormSubmit);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.root.addEventListener('change', this.onOptionChange);
      this.root.addEventListener('input', this.onOptionChange);

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isPrimaryVisible = !!entry?.isIntersecting && entry.intersectionRatio > 0.15;
          this.updateVisibility();
        },
        { threshold: [0, 0.15, 1] }
      );
      this.intersectionObserver.observe(this.primaryButton);

      this.mutationObserver = new MutationObserver(this.syncButtonState);
      this.mutationObserver.observe(this.primaryButton, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled'],
      });

      if (this.priceSource) {
        this.priceObserver = new MutationObserver(this.syncButtonState);
        this.priceObserver.observe(this.priceSource, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      }

      if (this.customizePanel) {
        this.customizeObserver = new MutationObserver(this.syncButtonState);
        this.customizeObserver.observe(this.customizePanel, {
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }

      const variantSource = this.root.querySelector('variant-selects');
      if (variantSource) {
        this.variantObserver = new MutationObserver(this.syncButtonState);
        this.variantObserver.observe(variantSource, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== String(this.sectionId)) return;
          this.syncButtonState();
        });
      }

      this.syncButtonState();
      this.updateVisibility();
    }

    destroy() {
      this.button?.removeEventListener('click', this.onStickyClick);
      this.topButton?.removeEventListener('click', this.onTopClick);
      this.form?.removeEventListener('submit', this.onFormSubmit);
      window.removeEventListener('scroll', this.onScroll);
      this.root.removeEventListener('change', this.onOptionChange);
      this.root.removeEventListener('input', this.onOptionChange);
      this.intersectionObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.variantObserver?.disconnect();
      this.customizeObserver?.disconnect();
      this.variantUnsubscriber?.();
    }

    onStickyClick() {
      if (this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true') return;
      this.primaryButton.click();
    }

    onTopClick() {
      const target = this.root.querySelector(`#ProductInfo-${this.sectionId}`) || this.root;
      const rect = target.getBoundingClientRect();
      const top = window.scrollY + rect.top - 92;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    onFormSubmit() {
      this.syncButtonState();
    }

    onScroll() {
      this.updateVisibility();
    }

    onOptionChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest('variant-selects') && !target.matches('[data-customize-input]')) return;
      this.syncButtonState();
    }

    updateVisibility() {
      if (!this.container) return;
      const shouldShow = !this.isPrimaryVisible && window.scrollY > 140;
      this.container.classList.toggle('is-visible', shouldShow);
      this.topButton?.classList.toggle('is-visible', shouldShow);
    }

    syncButtonState() {
      if (!this.button || !this.primaryButton) return;

      const sourceLabel = this.primaryButton.querySelector('span');
      const sourceText = sourceLabel?.textContent?.trim();
      if (sourceText) {
        this.label.textContent = sourceText;
      }

      const isDisabled =
        this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true';
      this.button.disabled = isDisabled;

      const isLoading = this.primaryButton.classList.contains('loading');
      this.button.classList.toggle('loading', isLoading);
      this.button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

      this.syncMeta();
    }

    syncMeta() {
      if (this.titleEl && this.titleSource) {
        const title = this.titleSource.textContent?.trim();
        if (title) this.titleEl.textContent = title;
      }

      if (this.ratingEl) {
        if (this.ratingSource) {
          const ratingText = this.ratingSource.querySelector('.rating-text');
          const ratingCount = this.ratingSource.querySelector('.rating-count');
          const ratingTextValue = ratingText?.textContent?.trim() || '';
          const ratingCountValue = ratingCount?.textContent?.trim() || '';
          const scoreMatch = ratingTextValue.match(/(\d+(?:\.\d+)?)/);
          const scaleMatch = ratingTextValue.match(/\/\s*(\d+(?:\.\d+)?)/);
          const score = scoreMatch ? parseFloat(scoreMatch[1]) : 0;
          const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 5;
          const clamped = Math.min(5, Math.max(0, Math.round((score / Math.max(scale, 1)) * 5)));
          const starsFilled = '★'.repeat(clamped);
          const starsEmpty = '☆'.repeat(5 - clamped);
          const stars = `${starsFilled}${starsEmpty}`;
          const details = [ratingTextValue, ratingCountValue].filter(Boolean).join(' ');

          if (details || stars) {
            this.ratingEl.innerHTML = `<span class="product-sticky-atc__stars" aria-hidden="true">${stars}</span><span class="product-sticky-atc__rating-text">${details}</span>`;
            this.ratingEl.hidden = false;
          } else {
            this.ratingEl.hidden = true;
            this.ratingEl.innerHTML = '';
          }
        } else {
          this.ratingEl.hidden = true;
          this.ratingEl.innerHTML = '';
        }
      }

      if (this.sizeEl) {
        const selectedSize = this.readSelectedOptionValueByKeywords([
          'size',
          '尺码',
          '尺寸',
          'tamanho',
          'taglia',
        ]);
        if (selectedSize) {
          this.sizeEl.textContent = `Size: ${selectedSize}`;
          this.sizeEl.hidden = false;
        } else {
          this.sizeEl.hidden = true;
          this.sizeEl.textContent = '';
        }
      }

      if (this.customizeEl) {
        const selectedCustomize = this.readSelectedOptionValueByKeywords([
          'customize',
          'custom',
          'personal',
          'print',
          '定制',
          '自定义',
          '印号',
        ]);
        if (selectedCustomize) {
          const customizeDetails = this.getCustomizeDetails();
          this.customizeEl.textContent = customizeDetails
            ? `Customize: ${selectedCustomize} · ${customizeDetails}`
            : `Customize: ${selectedCustomize}`;
          this.customizeEl.hidden = false;
        } else {
          this.customizeEl.hidden = true;
          this.customizeEl.textContent = '';
        }
      }

      if (!this.priceEl || !this.priceSource) return;
      const salePrice = this.priceSource.querySelector('.price-item--sale');
      const regularOnSale = this.priceSource.querySelector('.price__sale .price-item--regular');
      const regularPrice = this.priceSource.querySelector('.price__regular .price-item--regular');
      const activePrice = salePrice?.textContent?.trim() || regularPrice?.textContent?.trim() || '';
      this.priceEl.textContent = activePrice;

      const compareText = regularOnSale?.textContent?.trim() || '';
      this.compareEl.hidden = !compareText;
      this.compareEl.textContent = compareText;
    }

    readSelectedOptionValueByKeywords(keywords) {
      const variantSource = this.root.querySelector('variant-selects');
      if (!variantSource) return '';

      const hasKeyword = (value) => {
        const key = (value || '').trim().toLowerCase();
        return keywords.some((keyword) => key.includes(keyword));
      };

      const selectedVariantScript = variantSource.querySelector('script[data-selected-variant]');
      let selectedVariant = null;
      try {
        selectedVariant = selectedVariantScript ? JSON.parse(selectedVariantScript.textContent || '{}') : null;
      } catch (e) {
        selectedVariant = null;
      }

      const optionNames = [];
      if (this.form) {
        const optionsJsonScript = this.form.querySelector('[data-product-options-json]');
        try {
          const parsed = optionsJsonScript ? JSON.parse(optionsJsonScript.textContent || '[]') : [];
          if (Array.isArray(parsed)) optionNames.push(...parsed);
        } catch (e) {}
      }

      if (selectedVariant) {
        for (let i = 0; i < 3; i += 1) {
          const optionName = optionNames[i] || '';
          if (!hasKeyword(optionName)) continue;
          const variantValue = selectedVariant[`option${i + 1}`];
          if (variantValue) return String(variantValue).trim();
        }
      }

      const fieldsets = variantSource.querySelectorAll('fieldset.product-form__input');
      for (const fieldset of fieldsets) {
        const legend = fieldset.querySelector('legend');
        const legendText = legend?.childNodes?.[0]?.textContent || legend?.textContent || '';
        if (!hasKeyword(legendText)) continue;

        const selectedValue = legend?.querySelector('[data-selected-value]')?.textContent?.trim();
        if (selectedValue) return selectedValue;

        const selectedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          const label = fieldset.querySelector(`label[for="${selectedRadio.id}"]`);
          const labelText = label?.textContent?.trim();
          if (labelText) return labelText;
        }
      }

      const selects = variantSource.querySelectorAll('select[name^="options["]');
      for (const select of selects) {
        const nameMatch = select.name.match(/^options\[(.+)\]$/);
        const optionName = nameMatch ? nameMatch[1] : '';
        if (!hasKeyword(optionName)) continue;
        const chosen = select.options[select.selectedIndex]?.textContent?.trim();
        if (chosen) return chosen;
      }

      return '';
    }

    getCustomizeDetails() {
      const panelVisible = this.customizePanel && !this.customizePanel.hidden;
      if (!panelVisible) return '';

      const name = this.customizeNameInput?.value?.trim() || '';
      const number = this.customizeNumberInput?.value?.trim() || '';
      if (!name && !number) return '';
      if (name && number) return `${name} ${number}`;
      if (name) return name;
      return number;
    }
  }

  const initProductStickyAtc = (root) => {
    if (!(root instanceof HTMLElement)) return;
    if (instances.has(root)) return;

    const instance = new ProductStickyAtc(root);
    instance.init();
    if (!instance.container || !instance.button || !instance.primaryButton || !instance.form) return;
    instances.set(root, instance);
  };

  const initializeAll = () => {
    document.querySelectorAll('product-info[id^="MainProduct-"]').forEach(initProductStickyAtc);
  };

  document.addEventListener('DOMContentLoaded', initializeAll);
  document.addEventListener('product-info:loaded', (event) => {
    initProductStickyAtc(event.target);
  });
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('product-info[id^="MainProduct-"]').forEach(initProductStickyAtc);
  });
})();
